// Einheit-Widgets und Bereiche (README §8). Reihenfolge innerhalb der Gruppen wie im Prototyp,
// Beschriftungen wie in „Widgets anpassen“ (Scope u). Ein Klick öffnet ?bereich=<key>.

import {
  Armchair, Bank, Handshake, ChartBarHorizontal, ClockCounterClockwise, CrownSimple, CurrencyEur, EnvelopeSimpleOpen, FolderSimple, Gauge,
  Key, MapPinArea, NotePencil, PencilSimple, PiggyBank, Ruler, ShieldCheck, Signature, Ticket, TrendUp, Umbrella, User, UsersThree,
  Vault, Wallet, type Icon,
} from '@phosphor-icons/react';
import type { GruppenKey, WidgetDef } from '../../lib/immobilien/widgetRaster';
import type { WidgetGroesse } from './ui/Widget';

export type EinheitWidgetKey =
  | 'mieter' | 'gesamt' | 'flaeche' | 'ausst'
  | 'kaution' | 'mv' | 'mieterh' | 'keys'
  | 'konto' | 'zus' | 'kauf' | 'mahn' | 'hausgeld' | 'grund' | 'bank'
  | 'zaehler' | 'wartung' | 'vertraege' | 'versich' | 'tickets'
  | 'eig' | 'etv' | 'protokoll' | 'notiz' | 'docs';

/** 'edit' = Einheit bearbeiten, 'mvedit' = Mietverhältnis bearbeiten (?mieter=<id>) – beide ohne eigenes Widget. */
export type EinheitBereichKey = EinheitWidgetKey | 'edit' | 'mvedit';

export interface EinheitWidgetDef extends WidgetDef<EinheitWidgetKey> {
  groesse: WidgetGroesse;
}

const W = (key: EinheitWidgetKey, label: string, gruppe: GruppenKey, groesse: WidgetGroesse = 'normal'): EinheitWidgetDef => ({ key, label, gruppe, groesse });

export const EINHEIT_WIDGETS: readonly EinheitWidgetDef[] = [
  W('mieter', 'Mieter', 'ueb', 'breit'),
  W('gesamt', 'Gesamtmiete', 'ueb'),
  W('flaeche', 'Fläche', 'ueb'),
  W('ausst', 'Ausstattung', 'ueb', 'breit'),
  W('kaution', 'Kaution', 'verm'),
  W('mv', 'Mietverhältnisse', 'verm', 'gross'),
  W('mieterh', 'Mieterhöhung', 'verm'),
  W('keys', 'Schlüssel', 'verm'),
  W('konto', 'Mietkonto', 'fin'),
  W('zus', 'Mietzusammensetzung', 'fin', 'breit'),
  W('kauf', 'Kauf & Finanzierung', 'fin', 'breit'),
  W('mahn', 'Mahnung', 'fin'),
  W('hausgeld', 'Hausgeld', 'fin'),
  W('grund', 'Grundsteuer', 'fin'),
  W('bank', 'Zahlungsweg', 'fin'),
  W('zaehler', 'Zähler', 'tech', 'breit'),
  W('wartung', 'Prüfungen', 'tech'),
  W('vertraege', 'Verträge', 'tech'),
  W('versich', 'Versicherung', 'tech'),
  W('tickets', 'Tickets', 'tech', 'breit'),
  W('eig', 'Eigentümer', 'recht'),
  W('etv', 'ETV', 'recht'),
  W('protokoll', 'Aktivität', 'recht', 'breit'),
  W('notiz', 'Notiz', 'recht'),
  W('docs', 'Dokumente', 'recht', 'breit'),
];

/** Kopf des Bereichs: Titel · Icon (README §8). */
export const BEREICH_KOPF: Record<EinheitBereichKey, { titel: string; icon: Icon }> = {
  mieter: { titel: 'Mieter', icon: User },
  gesamt: { titel: 'Gesamtmiete & Entwicklung', icon: CurrencyEur },
  flaeche: { titel: 'Fläche & Räume', icon: Ruler },
  ausst: { titel: 'Ausstattung', icon: Armchair },
  mv: { titel: 'Mietverhältnisse', icon: ClockCounterClockwise },
  kaution: { titel: 'Kaution', icon: Vault },
  mieterh: { titel: 'Mieterhöhung', icon: TrendUp },
  keys: { titel: 'Schlüssel', icon: Key },
  konto: { titel: 'Mietkonto', icon: Wallet },
  zus: { titel: 'Mietzusammensetzung', icon: ChartBarHorizontal },
  mahn: { titel: 'Mahnwesen', icon: EnvelopeSimpleOpen },
  bank: { titel: 'Zahlungsweg & SEPA', icon: Bank },
  hausgeld: { titel: 'Hausgeld', icon: PiggyBank },
  grund: { titel: 'Grundbuch & Grundsteuer', icon: MapPinArea },
  kauf: { titel: 'Kauf & Finanzierung', icon: Bank },
  tickets: { titel: 'Tickets', icon: Ticket },
  zaehler: { titel: 'Zähler', icon: Gauge },
  wartung: { titel: 'Prüfungen & Wartung', icon: ShieldCheck },
  vertraege: { titel: 'Verträge & Kostenanteil', icon: Signature },
  versich: { titel: 'Versicherungen', icon: Umbrella },
  eig: { titel: 'Eigentümer', icon: CrownSimple },
  etv: { titel: 'Eigentümerversammlung', icon: UsersThree },
  docs: { titel: 'Dokumente', icon: FolderSimple },
  notiz: { titel: 'Notizen', icon: NotePencil },
  protokoll: { titel: 'Aktivität', icon: ClockCounterClockwise },
  edit: { titel: 'Einheit bearbeiten', icon: PencilSimple },
  mvedit: { titel: 'Mietverhältnis bearbeiten', icon: Handshake },
};

/**
 * Bereiche, die schon mit echten Daten arbeiten. Die übrigen erscheinen im Raster mit
 * „Wird noch angebunden“ bzw. „Startet mit der Buchhaltung“ und sind nicht klickbar.
 */
export const ANGEBUNDENE_BEREICHE: ReadonlySet<EinheitBereichKey> = new Set<EinheitBereichKey>([
  'mieter', 'gesamt', 'flaeche', 'ausst', 'mv', 'kaution', 'konto', 'zus', 'bank', 'hausgeld', 'kauf',
  'zaehler', 'eig', 'notiz', 'docs',
  // E8
  'tickets', 'wartung', 'versich', 'vertraege', 'etv', 'keys', 'protokoll', 'mieterh',
]);

export function istBereichKey(wert: string | null): wert is EinheitBereichKey {
  return wert !== null && Object.prototype.hasOwnProperty.call(BEREICH_KOPF, wert);
}
